/**
 * How a view is ordered.
 *
 * The terms are applied in the order they are listed, which is the order the
 * backend applies them in: the first is the ordering, and each one after it
 * only separates the rows the ones before could not. That is why the rows are
 * labelled "sort by" and "then by" rather than numbered -- the sequence is the
 * meaning, not decoration.
 */
import { ArrowDownUp, GripVertical, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import type { Field, ViewSort } from '@/api/types'
import type { RowsView } from '../views/field-order'
import { canSortInView } from '../fields/behavior'
import { Button } from '@/shared/ui/button'
import { NativeSelect, NativeSelectOption } from '@/shared/ui/native-select'
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/ui/popover'
import { verticalListSortingStrategy } from '@dnd-kit/sortable'
import { restrictToVerticalAxis } from '@dnd-kit/modifiers'
import { SortableList } from '@/shared/components/sortable'
import { useSortableItem } from '@/shared/hooks/use-sortable-item'
import { useViewSorts, type SortDirection } from './sort-mutations'

export function SortMenu({
  tableId,
  view,
  fields,
}: {
  tableId: number
  view: RowsView
  fields: readonly Field[]
}) {
  const { t } = useTranslation()
  const sorts = view.sortings ?? []
  const { add, change, remove, prioritize } = useViewSorts(tableId, view.id)

  // A field a view cannot be ordered by at all -- a file, a password, a formula
  // that produces neither -- is not offered. `canSortInView` asks the field's
  // own behaviour, which answers `null` for exactly those, so there is no
  // second list here to fall out of step with the one the ordering uses.
  const sortable = fields.filter((field) => canSortInView(field))
  const used = new Set(sorts.map((sort) => sort.field))
  const free = sortable.filter((field) => !used.has(field.id))

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="sm" className="gap-1.5">
          <ArrowDownUp className="size-4 opacity-70" />
          {sorts.length === 0
            ? t('view.sort')
            : t('view.sortCount', { count: sorts.length })}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 p-2">
        {sorts.length === 0 ? (
          <p className="px-2 py-1.5 text-sm text-muted-foreground">
            {t('view.sortNone')}
          </p>
        ) : (
          <ul aria-label={t('view.sort')} className="flex flex-col gap-1">
            <SortableList
              ids={sorts.map((sort) => sort.id)}
              strategy={verticalListSortingStrategy}
              modifiers={[restrictToVerticalAxis]}
              onReorder={prioritize}
            >
              {sorts.map((sort, index) => (
                <SortRow
                  key={sort.id}
                  sort={sort}
                  index={index}
                  fields={fields}
                  free={free}
                  onChange={change}
                  onRemove={remove}
                />
              ))}
            </SortableList>
          </ul>
        )}

        <div className="mt-2 border-t pt-2">
          {free.length === 0 ? (
            <p className="px-2 py-1 text-xs text-muted-foreground">
              {t('view.sortNoFields')}
            </p>
          ) : (
            <NativeSelect
              size="sm"
              className="w-full"
              aria-label={t('view.sortAdd')}
              // Never keeps what was chosen: choosing is the whole action, and
              // the row it creates is where the choice then lives.
              value=""
              onChange={(event) => {
                add(Number(event.target.value), 'ASC')
              }}
            >
              <NativeSelectOption value="" disabled>
                {t('view.sortAdd')}
              </NativeSelectOption>
              {free.map((field) => (
                <NativeSelectOption key={field.id} value={field.id}>
                  {field.name}
                </NativeSelectOption>
              ))}
            </NativeSelect>
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}

/**
 * One term, and the handle that moves it.
 *
 * A component rather than a `<li>` in the loop above because the drag hook is a
 * hook. The handle is its own control, for the reason the plan gives: a row
 * that is both a drag source and a set of form fields cannot tell
 * Space-to-pick-up from Space-inside-a-select.
 */
function SortRow({
  sort,
  index,
  fields,
  free,
  onChange,
  onRemove,
}: {
  sort: ViewSort
  index: number
  fields: readonly Field[]
  /** The fields not already sorted by, which is what may be chosen instead. */
  free: readonly Field[]
  onChange: (sortId: number, changes: { field?: number; order?: SortDirection }) => void
  onRemove: (sortId: number) => void
}) {
  const { t } = useTranslation()
  const { setNodeRef, style, handleProps, isDragging } = useSortableItem(sort.id)

  // The sort's own field is offered even when it is not in `free`: a formula
  // whose expression changed can stop being sortable while a view is still
  // ordered by it, and a select with no option for its own value shows the
  // wrong field.
  const own = fields.find((field) => field.id === sort.field)

  return (
    <li
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-2 ${isDragging ? 'opacity-60' : ''}`}
    >
      <button
        type="button"
        aria-label={t('view.sortMove', { field: own?.name ?? '' })}
        className="cursor-grab rounded p-0.5 text-muted-foreground hover:bg-muted"
        {...handleProps}
      >
        <GripVertical className="size-4" />
      </button>
      {/* The label says what the term *does*, which is a function of where it
          is in the list -- so it changes as soon as the list does, and is the
          only thing on the row that says a drag landed. */}
      <span className="w-14 shrink-0 text-xs text-muted-foreground">
        {index === 0 ? t('view.sortBy') : t('view.thenBy')}
      </span>
      <NativeSelect
        size="sm"
        className="flex-1"
        aria-label={t('view.sortField', { position: index + 1 })}
        value={sort.field}
        onChange={(event) => {
          onChange(sort.id, { field: Number(event.target.value) })
        }}
      >
        {(own === undefined ? free : [own, ...free]).map((field) => (
          <NativeSelectOption key={field.id} value={field.id}>
            {field.name}
          </NativeSelectOption>
        ))}
      </NativeSelect>
      <NativeSelect
        size="sm"
        aria-label={t('view.sortDirection', { field: own?.name ?? '' })}
        value={sort.order ?? 'ASC'}
        onChange={(event) => {
          onChange(sort.id, { order: event.target.value as SortDirection })
        }}
      >
        <NativeSelectOption value="ASC">{t('view.ascending')}</NativeSelectOption>
        <NativeSelectOption value="DESC">{t('view.descending')}</NativeSelectOption>
      </NativeSelect>
      <button
        type="button"
        aria-label={t('view.sortRemove', { field: own?.name ?? '' })}
        onClick={() => {
          onRemove(sort.id)
        }}
        className="rounded p-1 text-muted-foreground hover:bg-muted"
      >
        <X className="size-3.5" />
      </button>
    </li>
  )
}
